import copy from "clipboard-copy";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../parts/SvgIcon.js";
import { useRillContext } from "../../../rill/RillReact/share/base.js";
import { getUserToRequest } from "../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import PlansTable from "./components/PlansTable.js";

const tabs = {
  PULL: "pull",
  PUSH: "push",
  PRESS: "press",
};
const PlanManager = (props) => {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useRillContext();

  const { activeUser, planManager, organizationOwner } = locals;

  const userToRequest = getUserToRequest(activeUser, organizationOwner);
  const { pressAccountInfo, pushAccountInfo, pressCustomBranding } =
    userToRequest;
  const { pressPlans, pushPlans } = planManager;
  const isPressCoach = !!pressAccountInfo;
  const isPushCoach = !!pushAccountInfo;
  const [selectedTab, setSelectedTab] = useState(tabs.PUSH);

  // msb-membership (PRESS) reads the plan from a catch-all route segment;
  // msb-storefront (PUSH) is a flat /checkout route, so its plan travels as a
  // query param instead. Shared by the copy-link action and the QR code,
  // which both need the same URL without duplicating this branch.
  const getPaymentLinkUrl = (planId, domain = "gymmembership.app") => {
    const { subdomain } = pushAccountInfo;
    return domain === "gymmembership.app"
      ? `https://${subdomain}.${domain}/checkout/${planId}`
      : `https://${subdomain}.${domain}/checkout?plan=${planId}`;
  };

  const getPaymentLink = async (e, planId, domain = "gymmembership.app") => {
    e.preventDefault();
    await copy(getPaymentLinkUrl(planId, domain));
    submit("/actions/refresh", {
      method: "GET",
      body: {
        copied: true,
      },
    });
  };

  const updatePlanStatus = (id, status) => {
    submit(`/actions/plan-manager/status`, {
      method: "POST",
      body: { status: !status ? "unpublish" : "publish", id },
    });
  };

  const preferPlan = (e, id, preferred = "false", plans) => {
    e.preventDefault();
    submit(`/actions/plan-manager/preferred`, {
      method: "POST",
      body: { id, preferred, plans },
    });
  };

  const reorderPlans = (order, type) => {
    submit(`/actions/plan-manager/reorder`, {
      method: "POST",
      body: { order, type },
    });
  };

  const togglePrivate = (e, id, isPrivate) => {
    e.preventDefault();
    submit(`/actions/plan-manager/private`, {
      method: "POST",
      body: { id, private: isPrivate },
    });
  };

  return (
    <main className="plan-manager">
      <header>
        {/* biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: styled tab bar (Pico tablist) */}
        <nav role="tablist">
          {isPushCoach && (
            // biome-ignore lint/a11y/useValidAnchor: tab switch, href kept for styling
            <a
              aria-current={selectedTab === tabs.PUSH ? true : undefined}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedTab(tabs.PUSH);
              }}
            >
              {t("planManager.tabSubscriptionPlans")}
            </a>
          )}
          {isPressCoach && (
            // biome-ignore lint/a11y/useValidAnchor: tab switch, href kept for styling
            <a
              aria-current={selectedTab === tabs.PRESS ? true : undefined}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedTab(tabs.PRESS);
              }}
            >
              {t("planManager.tabGymMembershipPlans")}
            </a>
          )}
          <hgroup>
            {isPressCoach && selectedTab === tabs.PRESS && props.csvPress && (
              // biome-ignore lint/a11y/useSemanticElements: Pico styles links as buttons
              <a
                role="button"
                data-button-type="outline"
                target="_blank"
                href={`data:attachment/csv,${encodeURIComponent(
                  props.csvPress
                )}`}
                download="plans.csv"
                sz="small"
              >
                <span>{t("planManager.exportToCsv")}</span>
              </a>
            )}
            {isPressCoach && selectedTab === tabs.PRESS && (
              // biome-ignore lint/a11y/useSemanticElements: Pico styles links as buttons
              <a
                role="button"
                data-button-type="secondary"
                className="manage"
                href="/coaching-portal/settings/gym-locations"
                sz="small"
              >
                {t("planManager.manageGymLocations")}
              </a>
            )}
            {/* Coupons are account-wide in Stripe — the same codes work at
                PUSH and PRESS checkout (sc-46003). */}
            {(selectedTab === tabs.PUSH || selectedTab === tabs.PRESS) && (
              // biome-ignore lint/a11y/useSemanticElements: Pico styles links as buttons
              <a
                role="button"
                data-button-type="secondary"
                href={`/actions/refresh?modal[type]=create-coupon`}
                sz="small"
                data-icon
                className="create-coupon"
              >
                <SvgIcon src="/img/new-icons/ticket.svg" />
                {t("planManager.createCoupon")}
              </a>
            )}
            {(isPressCoach || isPushCoach) && selectedTab && (
              // biome-ignore lint/a11y/useSemanticElements: Pico styles links as buttons
              <a
                role="button"
                data-button-type="primary"
                href={`/actions/refresh?modal[type]=create-plan&type=${selectedTab}`}
                sz="small"
                data-icon
              >
                <img
                  src="/img/new-icons/add.svg"
                  alt={t("planManager.addIconAlt")}
                />
                {t("planManager.addNew")}
              </a>
            )}
          </hgroup>
        </nav>
      </header>
      {isPressCoach && selectedTab === tabs.PRESS && (
        <PlansTable
          title={t("planManager.titleGymMembershipPlans")}
          isPress
          plans={pressPlans}
          getPaymentLink={getPaymentLink}
          getPaymentLinkUrl={getPaymentLinkUrl}
          updatePlanStatus={updatePlanStatus}
          preferPlan={preferPlan}
          togglePrivate={togglePrivate}
          reorderPlans={reorderPlans}
          gymLocations={pressCustomBranding?.gymLocations}
          csv={props.csvPress}
        />
      )}

      {isPushCoach && selectedTab === tabs.PUSH && (
        <PlansTable
          title={t("planManager.titleSubscriptionPlans")}
          plans={pushPlans}
          getPaymentLink={getPaymentLink}
          getPaymentLinkUrl={getPaymentLinkUrl}
          updatePlanStatus={updatePlanStatus}
          preferPlan={preferPlan}
          togglePrivate={togglePrivate}
          reorderPlans={reorderPlans}
          planType="push"
        />
      )}
    </main>
  );
};

export default PlanManager;
